/**
 * useFieldSearch — TanStack Query hook для поиска поля на карте по имени (AK-378).
 *
 * Endpoint: GET /api/v1/fields?search=<q>&farm_id=<id>&limit=20
 *
 * Backend уже умеет `ILIKE` по имени (list_fields, `search`-параметр) и с
 * AK-378 отдаёт `centroid: [lng, lat]` (WGS84) для мгновенного `flyTo` без
 * второго запроса за геометрией. Поиск ВСЕГДА скоупится по farm_id — предусловие
 * фичи (лупа неактивна вне farm-scope).
 *
 * enabled-гейт: пустой query (после trim) или отсутствие farmId → hook idle,
 * запрос не уходит. Debounce делает вызывающий компонент (панель поиска), сюда
 * приходит уже debounced-значение.
 *
 * staleTime 30s: имена полей меняются редко; в пределах одной поисковой сессии
 * не перезапрашиваем при повторном вводе того же префикса.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Одно совпадение поиска — минимальный набор для строки дропдауна + flyTo. */
export interface FieldSearchItem {
  id: string;
  name: string;
  number: string | null;
  area_ha: number | null;
  current_crop: string | null;
  /** Центроид [lng, lat] (WGS84). null когда у поля ещё нет геометрии. */
  centroid: [number, number] | null;
}

interface FieldListResponse {
  total: number;
  items: Array<{
    id: string;
    name: string;
    number: string | null;
    area_ha: number | null;
    current_crop: string | null;
    centroid: number[] | null;
  }>;
}

const SEARCH_LIMIT = 20;

export function useFieldSearch(
  query: string,
  farmId: string | null,
): UseQueryResult<FieldSearchItem[], Error> {
  const trimmed = query.trim();
  const enabled = trimmed.length > 0 && !!farmId;

  return useQuery<FieldSearchItem[], Error>({
    queryKey: ['field-search', farmId ?? '-', trimmed],
    enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        search: trimmed,
        farm_id: farmId as string,
        limit: String(SEARCH_LIMIT),
      });
      const resp = await api.get<FieldListResponse>(
        `/api/v1/fields?${params.toString()}`,
      );
      return resp.items.map((it) => ({
        id: it.id,
        name: it.name,
        number: it.number ?? null,
        area_ha: it.area_ha ?? null,
        current_crop: it.current_crop ?? null,
        centroid:
          Array.isArray(it.centroid) && it.centroid.length === 2
            ? [it.centroid[0], it.centroid[1]]
            : null,
      }));
    },
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}
